import { backendURL } from '@/config';
import {
  ApiBodyError,
  CiviCrmUncontactableError,
  DatabaseUncontactableError,
  ServerUncontactableError,
} from '@/errors';
import { useAccessToken } from '@/hooks/useAccessToken';
import { logger } from '@/util';
import {
  backendStatusAtom,
  isDependencyDownReason,
  markBackendUp as markSharedBackendUp,
  setBackendDown,
  type MarkBackendUpOptions,
} from '@australiangreens/ag-internal-components';
import { useAtomValue } from 'jotai';

export { backendStatusAtom };

/**
 * True while a dependency outage (Redis / Civi / DB) is latched. Callers must
 * not start writes. SERVER_UNCONTACTABLE is excluded — a single failed probe
 * (e.g. /currentuserinfo) must not freeze assign/save controls.
 */
export function useRedisWritesBlocked(): boolean {
  const { status, reason } = useAtomValue(backendStatusAtom);
  return status === 'DOWN' && isDependencyDownReason(reason);
}

export default async function callBackendApi<
  Data = Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Result = any,
>(options: {
  token: string;
  method: 'get' | 'delete' | 'post' | 'put';
  endpoint: string;
  data?: Data;
  extraConfig?: Record<string, unknown>;
  abortSignal?: AbortSignal;
}): Promise<Result> {
  const { token, method, endpoint, data, extraConfig, abortSignal } = options;

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const body = data ? JSON.stringify(data) : undefined;

  if (['post', 'put'].includes(method)) {
    Object.assign(headers, { 'Content-Type': 'application/json' });
  }

  return performFetch(
    endpoint,
    {
      method,
      body,
      headers,
      signal: abortSignal,
      ...extraConfig,
    },
    { clearRedisOnSuccess: true }
  );
}

export async function callBackendApiWithoutAuth(options: {
  method: 'get';
  endpoint: 'hashz' | '/hashz' | 'livez' | '/livez' | 'readyz' | '/readyz';
  extraConfig?: Record<string, unknown>;
  abortSignal?: AbortSignal;
}) {
  const { method, endpoint, abortSignal, extraConfig } = options;

  return performFetch(
    endpoint,
    {
      method,
      signal: abortSignal,
      ...extraConfig,
    },
    { clearRedisOnSuccess: false }
  );
}

/**
 * Clears LM-unreachable / unknown DOWN states after a successful response.
 * Authenticated successes also clear a latched Redis outage. Civi and Database
 * stay latched until their recovery probes pass clearCivi / clearDatabase.
 */
function markBackendUp(options: MarkBackendUpOptions = {}) {
  markSharedBackendUp(options);
}

function firstErrorNames(error: {
  name?: string;
  hierarchicalName?: string;
}): string {
  return [error.name, error.hierarchicalName].filter(Boolean).join(':');
}

async function performFetch(
  endpoint: string,
  init: RequestInit,
  options: { clearRedisOnSuccess: boolean }
) {
  const resource = `${backendURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  try {
    const res = await fetch(resource, init);
    if (res.headers.get('content-type') === 'application/pdf') {
      markBackendUp(options.clearRedisOnSuccess ? { clearRedis: true } : {});
      return await res.blob();
    }
    if (!res.ok) {
      const body: { errors: { name: string; message: string; hierarchicalName: string }[] } =
        await res.json();
      if (res.status === 503 && body.errors.length) {
        const first = body.errors[0];
        const firstNames = firstErrorNames(first);
        // Prefer name/hierarchy — instanceof can fail across duplicate package copies.
        if (firstNames.includes('CiviApiAxiosError')) {
          const error = new CiviCrmUncontactableError(`${res.status}: ${res.statusText}`);
          setBackendDown('CIVI_CRM_UNCONTACTABLE', first);
          throw error;
        }
        if (firstNames.includes('RedisConnectionError')) {
          setBackendDown('REDIS_UNCONTACTABLE', first);
          // Fail the query so React Query retries; do not treat as success with undefined data.
          throw new ApiBodyError(`${res.status}: ${res.statusText}`, body);
        }
        if (firstNames.includes('DatabaseUncontactableError')) {
          const error = new DatabaseUncontactableError(`${res.status}: ${res.statusText}`);
          setBackendDown('DATABASE_UNCONTACTABLE', first);
          throw error;
        }
      }

      // We are not getting an error code in the 200-299 range. Let's throw an
      // API error.
      const serverBehavingBadlyError = new ApiBodyError(`${res.status}: ${res.statusText}`, body);
      throw serverBehavingBadlyError;
    }
    if (res.status === 204) {
      markBackendUp(options.clearRedisOnSuccess ? { clearRedis: true } : {});
      return;
    }
    const resjson = await res.json();
    markBackendUp(options.clearRedisOnSuccess ? { clearRedis: true } : {});

    return resjson;
  } catch (e) {
    // If we are in the catch block, either its our own error from the try => rethrow
    if (
      ['ApiBodyError', 'CiviCrmUncontactableError', 'DatabaseUncontactableError'].includes(
        (e as Error).name
      )
    ) {
      throw e;
    }

    // Or its an abort error => rethrow
    if ((e as Error).name === 'AbortError') {
      throw e;
    }

    // Otherwise we are unable to contact the service. Maybe its a local network
    // issue, or maybe the server is down. No way to know here, but we throw a
    // specific error that can be handled.
    // When the network has failed, it is a good time to tell Sentry.
    const error = new ServerUncontactableError((e as Error).message);
    setBackendDown('SERVER_UNCONTACTABLE', error);
    throw error;
  }
}

function handleFetchError(e: unknown): never {
  // Reasons are already latched in performFetch; keep this as the authenticated
  // rethrow boundary (and a safety net if a caller throws these directly).
  if (e instanceof ServerUncontactableError) setBackendDown('SERVER_UNCONTACTABLE', e);
  if (e instanceof CiviCrmUncontactableError) setBackendDown('CIVI_CRM_UNCONTACTABLE', e);
  if (e instanceof DatabaseUncontactableError) setBackendDown('DATABASE_UNCONTACTABLE', e);
  throw e;
}

export const useCallBackendApi = <Data, Result>(
  timingMessageA: string | undefined = undefined,
  timingMessageB: string | undefined = undefined
) => {
  const getAccessToken = useAccessToken();

  return async (params: Omit<Parameters<typeof callBackendApi<Data, Result>>[0], 'token'>) => {
    const token = await getAccessToken();

    try {
      if (timingMessageA) {
        logger.info(timingMessageA);
      }
      const t0 = performance.now();
      const thing = await callBackendApi<Data, Result>({ ...params, token });
      const timeMs = performance.now() - t0;
      if (timingMessageB) {
        logger.info(`${timingMessageB} [${Math.round(timeMs)}ms]`);
      }
      return thing;
    } catch (e) {
      handleFetchError(e);
    }
  };
};
